// Build mode: the web client's first real game action (building is asset
// management, allowed by the §4 trust profile — conquest/combat stay
// mobile-only). A signed-in noble viewing their own kingdom can toggle Build
// Mode from the kingdom details panel: unbuilt plots render as dirt lots with
// hammer icons (mobile parity), clicking one opens the build menu in the
// panel, and building POSTs /v1/structures — no GPS involved, the server
// gates on kingdom membership + gold.
import type { Map as MlMap, GeoJSONSource, PointLike } from 'maplibre-gl';
import type { Feature, MultiPolygon, Point, Polygon } from 'geojson';
import { api, ApiError } from '../api/client';
import type {
  CatalogItem,
  Me,
  MyKingdom,
  PlotCollection,
  PlotProperties,
} from '../api/models';
import { totemTextureImage } from './totem-materials';
import type { BuildContext, DetailsPanel, Selection } from './details-panel';

export const PLOTS_SOURCE = 'my-plots-unbuilt';
export const PLOTS_ICONS_SOURCE = 'my-plots-icons';
export const PLOTS_FILL_LAYER = 'my-plots-unbuilt-fill';
const PLOTS_DIRT_LAYER = 'my-plots-unbuilt-dirt';
const PLOTS_ICONS_LAYER = 'my-plots-icons-symbols';
const HAMMER_IMAGE = 'plot-hammer';

// Mobile parity (plots_layer.dart): flat dirt tone under the dirt-pattern
// wash; the pattern image resolves through the styleimagemissing handler.
const UNBUILT_DIRT = '#A8916D';

const EMPTY: PlotCollection = { type: 'FeatureCollection', features: [] };

// Plot features the player can build on, with the building id copied into
// properties (queryRenderedFeatures drops non-numeric feature ids). Pure for
// tests.
export function unbuiltFeatures(
  plots: PlotCollection,
): Array<Feature<Polygon | MultiPolygon, PlotProperties & { building_id: string }>> {
  return plots.features
    .filter((f) => f.properties.structure_type === undefined)
    .map((f) => ({
      ...f,
      properties: { ...f.properties, building_id: String(f.id) },
    }));
}

// One icon anchor point per unbuilt plot. Pure for tests.
export function iconPoints(
  plots: PlotCollection,
): Array<Feature<Point, Record<string, never>>> {
  return unbuiltFeatures(plots).map((f) => ({
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [f.properties.anchor_lng, f.properties.anchor_lat],
    },
    properties: {},
  }));
}

// The mobile hammer icon (plot_icon.dart) ported to canvas: brown circle,
// cream rim and hammer, drawn at 2x and displayed at half size.
function hammerImageData(): ImageData {
  const size = 72;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const w = size;
  ctx.fillStyle = 'rgba(74, 59, 38, 0.8)';
  ctx.beginPath();
  ctx.arc(w / 2, w / 2, w * 0.46, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#F2E9D0';
  ctx.lineWidth = w * 0.05;
  ctx.beginPath();
  ctx.arc(w / 2, w / 2, w * 0.46, 0, Math.PI * 2);
  ctx.stroke();
  ctx.lineCap = 'round';
  // Handle: diagonal stroke; head: perpendicular bar at its top.
  ctx.lineWidth = w * 0.1;
  ctx.beginPath();
  ctx.moveTo(w * 0.34, w * 0.68);
  ctx.lineTo(w * 0.6, w * 0.42);
  ctx.stroke();
  ctx.lineWidth = w * 0.16;
  ctx.beginPath();
  ctx.moveTo(w * 0.52, w * 0.3);
  ctx.lineTo(w * 0.72, w * 0.5);
  ctx.stroke();
  return ctx.getImageData(0, 0, size, size);
}

export interface BuildModeDeps {
  getToken(): Promise<string | null>;
  // Signed-in player, for the gold shown in the build menu; null signs out.
  getPlayer(): Me | null;
  // Push the post-build balance up so the account display can follow.
  onGold(gold: number): void;
  // Fired after a successful build so the public layers refresh immediately.
  onBuilt(): void;
}

export interface BuildMode {
  // Probe/forget the player's kingdom as the account state changes.
  onAccount(signedIn: boolean): void;
  // The details panel's window into build mode (actionsFor context).
  context(): BuildContext | undefined;
  // Resolve a map click to a plot selection while active (panel precedence).
  resolveClick(point: PointLike): Selection | null;
  attachPanel(panel: DetailsPanel): void;
}

export function createBuildMode(map: MlMap, deps: BuildModeDeps): BuildMode {
  let active = false;
  let kingdom: MyKingdom | null = null;
  let catalog: CatalogItem[] = [];
  let plots: PlotCollection = EMPTY;
  let panel: DetailsPanel | undefined;

  function setSource(id: string, features: unknown[]): void {
    const data = { type: 'FeatureCollection', features } as PlotCollection;
    const source = map.getSource(id) as GeoJSONSource | undefined;
    if (source) {
      source.setData(data);
      return;
    }
    map.addSource(id, { type: 'geojson', data });
  }

  function ensureLayers(): void {
    if (map.getLayer(PLOTS_FILL_LAYER)) return;
    if (!map.hasImage(HAMMER_IMAGE)) {
      map.addImage(HAMMER_IMAGE, hammerImageData());
    }
    map.addLayer({
      id: PLOTS_FILL_LAYER,
      type: 'fill',
      source: PLOTS_SOURCE,
      paint: { 'fill-color': UNBUILT_DIRT, 'fill-opacity': 0.35 },
    });
    map.addLayer({
      id: PLOTS_DIRT_LAYER,
      type: 'fill',
      source: PLOTS_SOURCE,
      paint: {
        'fill-pattern': totemTextureImage('dirt'),
        'fill-opacity': 0.55,
      },
    });
    map.addLayer({
      id: PLOTS_ICONS_LAYER,
      type: 'symbol',
      source: PLOTS_ICONS_SOURCE,
      layout: {
        'icon-image': HAMMER_IMAGE,
        'icon-size': 0.5,
        'icon-allow-overlap': true,
      },
    });
  }

  function render(): void {
    ensureLayers();
    const visible = active ? unbuiltFeatures(plots) : [];
    setSource(PLOTS_SOURCE, visible);
    setSource(PLOTS_ICONS_SOURCE, active ? iconPoints(plots) : []);
  }

  async function refreshPlots(): Promise<void> {
    if (!kingdom) return;
    const token = await deps.getToken();
    if (!token) return;
    plots = await api.kingdomPlots(token, kingdom.id);
    render();
  }

  async function activate(): Promise<void> {
    active = true;
    try {
      await refreshPlots();
    } catch {
      // Leave build mode rather than showing an empty, silently-broken state.
      active = false;
      render();
    }
  }

  function deactivate(): void {
    active = false;
    plots = EMPTY;
    render();
    panel?.close();
  }

  async function build(buildingId: string, type: string): Promise<void> {
    const token = await deps.getToken();
    if (!token) return;
    try {
      const res = await api.buildStructure(token, {
        building_id: buildingId,
        type,
      });
      deps.onGold(res.gold);
      panel?.close();
      await refreshPlots();
      deps.onBuilt();
    } catch (err) {
      // Re-show the plot with the server's message (409 someone built first,
      // 422 not enough gold, …) so the player isn't left guessing.
      const message =
        err instanceof ApiError ? err.message : 'The build failed — try again';
      const selection = plotSelection(buildingId);
      if (selection) panel?.show({ ...selection, error: message });
    }
  }

  function plotSelection(
    buildingId: string,
  ): Extract<Selection, { kind: 'plot' }> | null {
    const plot = unbuiltFeatures(plots).find(
      (f) => f.properties.building_id === buildingId,
    );
    if (!plot) return null;
    return {
      kind: 'plot',
      props: plot.properties,
      gold: deps.getPlayer()?.gold ?? 0,
    };
  }

  return {
    onAccount(signedIn: boolean): void {
      if (!signedIn) {
        kingdom = null;
        catalog = [];
        deactivate();
        return;
      }
      void (async () => {
        const token = await deps.getToken();
        if (!token) return;
        try {
          [kingdom, catalog] = await Promise.all([
            api.myKingdom(token),
            api.structureCatalog(token),
          ]);
        } catch {
          // No build controls this session; the map stays fully readable.
          kingdom = null;
        }
      })();
    },

    context(): BuildContext | undefined {
      if (!kingdom) return undefined;
      const kingdomId = kingdom.id;
      return {
        isMine: (id: string) => id === kingdomId,
        active,
        catalog,
        toggle: () => {
          if (active) {
            deactivate();
          } else {
            void activate();
          }
          // The open kingdom selection re-renders to flip the button label.
          panel?.refresh();
        },
        build: (buildingId: string, type: string) => void build(buildingId, type),
      };
    },

    resolveClick(point: PointLike): Selection | null {
      if (!active || map.getLayer(PLOTS_FILL_LAYER) === undefined) return null;
      const feature = map.queryRenderedFeatures(point, {
        layers: [PLOTS_FILL_LAYER],
      })[0];
      if (!feature) return null;
      const props = feature.properties as PlotProperties & {
        building_id: string;
      };
      return { kind: 'plot', props, gold: deps.getPlayer()?.gold ?? 0 };
    },

    attachPanel(p: DetailsPanel): void {
      panel = p;
    },
  };
}
